import React, { useState, useEffect, useCallback } from "react";
import "./stats.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import Chart from "../../components/chart/Chart";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Apartment,
  Build,
  Download,
  Add,
  Warning,
  CheckCircle,
  Schedule,
  Assessment,
  Receipt
} from "@mui/icons-material";
import { formatCurrency } from "../../utils/currencyFormatter";
import { generateEarningsReport, generateOccupancyReport, generateMaintenanceReport, generateFinancialReport } from "./reportGenerator";
import { calculateLateFees, addLateFee, getLateFeeStatistics, autoStackLateFees } from "./penaltyService";
import { paymentService } from '../../firebase/services';

const Stats = () => {
  const { payments, apartments, users, maintenance, refreshData } = useData();
  const { user } = useAuth();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [penaltyDialogOpen, setPenaltyDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [lateFeeAmount, setLateFeeAmount] = useState("");
  const [lateFeeReason, setLateFeeReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [viewLateFeeDialogOpen, setViewLateFeeDialogOpen] = useState(false);
  const [viewLateFee, setViewLateFee] = useState(null);

  // Automatically run late fee stacking for admins on mount
  useEffect(() => {
    if (user?.role === 'admin' && payments.length > 0) {
      (async () => {
        await autoStackLateFees(payments);
        // Optionally refresh data after stacking
        await refreshData();
      })();
    }
  }, [user, payments]);

  // Calculate statistics
  const stats = {
    totalRevenue: payments.reduce((sum, payment) => sum + (payment.amount || 0), 0),
    totalTenants: users.filter(u => u.role === 'tenant').length,
    totalApartments: apartments.length,
    occupiedApartments: apartments.filter(apt => apt.status === 'Occupied').length,
    pendingPayments: payments.filter(p => p.status === 'Pending').length,
    overduePayments: payments.filter(p => p.status === 'Overdue').length,
    maintenanceRequests: maintenance.length,
    pendingMaintenance: maintenance.filter(m => m.status === 'Pending').length
  };

  // Calculate monthly revenue for chart
  const monthlyRevenue = calculateMonthlyRevenue(payments);
  const occupancyRate = stats.totalApartments > 0 ? (stats.occupiedApartments / stats.totalApartments) * 100 : 0;

  // Calculate late fees for overdue payments
  const overduePayments = payments.filter(p => p.status === 'Overdue');
  const lateFees = calculateLateFees(overduePayments);
  const lateFeeStats = getLateFeeStatistics(payments);

  // Calculate payment type breakdown
  const paymentTypeBreakdown = calculatePaymentTypeBreakdown(payments);

  const handleGenerateReport = async () => {
    if (!selectedReport || !dateRange.start || !dateRange.end) {
      setMessage({ type: "error", text: "Please select a report type and date range" });
      return;
    }
    setLoading(true);
    try {
      let reportData;
      switch (selectedReport) {
        case "earnings":
          reportData = await generateEarningsReport(payments, dateRange, user);
          break;
        case "occupancy":
          reportData = await generateOccupancyReport(apartments, users, dateRange, user);
          break;
        case "maintenance":
          reportData = await generateMaintenanceReport(maintenance, dateRange, user);
          break;
        case "financial":
          reportData = await generateFinancialReport(payments, apartments, dateRange, user);
          break;
        default:
          throw new Error("Invalid report type");
      }
      setMessage({ type: "success", text: `Report generated successfully! File: ${reportData.fileName}` });
      setReportDialogOpen(false);
    } catch (error) {
      setMessage({ type: "error", text: `Error generating report: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLateFee = async () => {
    if (!selectedPayment || !lateFeeAmount || !lateFeeReason) {
      setMessage({ type: "error", text: "Please fill in all fields" });
      return;
    }

    setLoading(true);
    try {
      await addLateFee(selectedPayment, parseFloat(lateFeeAmount), lateFeeReason);
      setMessage({ type: "success", text: "Late fee added successfully!" });
      setPenaltyDialogOpen(false);
      setSelectedPayment(null);
      setLateFeeAmount("");
      setLateFeeReason("");
    } catch (error) {
      setMessage({ type: "error", text: `Error adding late fee: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card className="stat-card">
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={color}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <div className="stats">
      <Sidebar />
      <div className="statsContainer">
        <Navbar />
        {/* Header with action buttons */}
        <Box className="stats-header" mb={0}>
          <Typography variant="h4" component="h1">
            Dashboard Statistics
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => setReportDialogOpen(true)}
            >
              Generate Report
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Warning />}
                onClick={() => setPenaltyDialogOpen(true)}
              >
                Manage Late Fees
              </Button>
            )}
          </Box>
        </Box>

        {/* Alert Messages */}
        {message.text && (
          <Alert 
            severity={message.type} 
            onClose={() => setMessage({ type: "", text: "" })}
            sx={{ mb: 2 }}
          >
            {message.text}
          </Alert>
        )}

        {/* Stat Widgets Row */}
        <div className="stat-widgets">
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats.totalRevenue)}
            icon={<AttachMoney />}
            color="primary.main"
          />
          <StatCard
            title="Total Tenants"
            value={stats.totalTenants}
            icon={<People />}
            color="success.main"
          />
          <StatCard
            title="Occupancy Rate"
            value={`${occupancyRate.toFixed(1)}%`}
            icon={<Apartment />}
            color="info.main"
            subtitle={`${stats.occupiedApartments}/${stats.totalApartments} units`}
          />
          <StatCard
            title="Pending Maintenance"
            value={stats.pendingMaintenance}
            icon={<Build />}
            color="warning.main"
            subtitle={`${stats.maintenanceRequests} total requests`}
          />
        </div>

        {/* Payment Status Overview & Late Fees Summary */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card className="stat-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Status
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Chip 
                    icon={<CheckCircle />} 
                    label={`Paid: ${payments.filter(p => p.status === 'Paid').length}`}
                    color="success"
                  />
                  <Chip 
                    icon={<Schedule />} 
                    label={`Pending: ${stats.pendingPayments}`}
                    color="warning"
                  />
                  <Chip 
                    icon={<Warning />} 
                    label={`Overdue: ${stats.overduePayments}`}
                    color="error"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card className="stat-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Late Fees Summary
                </Typography>
                <Typography variant="h4" color="error.main">
                  {formatCurrency(lateFees.total)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {lateFees.count} overdue payments
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Potential: {formatCurrency(lateFeeStats.totalPotential)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Payment Type Breakdown & Late Fee Trends */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Card className="stat-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Types
                </Typography>
                <Chart 
                  title="Payment Type Distribution" 
                  aspect={2 / 1} 
                  data={paymentTypeBreakdown}
                  chartType="pie"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card className="stat-card">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Late Fee Trends
                </Typography>
                <Chart 
                  title="Monthly Late Fees" 
                  aspect={2 / 1} 
                  data={lateFeeStats.monthlyBreakdown.map(item => ({
                    name: item.name,
                    value: item.amount
                  }))}
                  chartType="bar"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Charts Row */}
        <div className="charts">
          <Chart title="Monthly Revenue" aspect={2 / 1} data={monthlyRevenue} />
          <Chart title="Occupancy Rate" aspect={2 / 1} data={[
            { name: "Occupied", value: stats.occupiedApartments },
            { name: "Available", value: stats.totalApartments - stats.occupiedApartments }
          ]} chartType="pie" />
        </div>

        {/* Report Generation Dialog */}
        <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Assessment />
              Generate Report
            </Box>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Report Type</InputLabel>
              <Select
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
                label="Report Type"
              >
                <MenuItem value="earnings">
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoney />
                    Earnings Report
                  </Box>
                </MenuItem>
                <MenuItem value="occupancy">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Apartment />
                    Occupancy Report
                  </Box>
                </MenuItem>
                <MenuItem value="maintenance">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Build />
                    Maintenance Report
                  </Box>
                </MenuItem>
                <MenuItem value="financial">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Receipt />
                    Comprehensive Financial Report
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Start Date"
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="End Date"
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleGenerateReport} 
              variant="contained" 
              disabled={loading}
              startIcon={loading ? null : <Download />}
            >
              {loading ? "Generating..." : "Generate"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Late Fee Management Dialog */}
        <Dialog open={penaltyDialogOpen} onClose={() => setPenaltyDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning />
              Manage Late Fees
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              All late fees are now automatically generated. You can view or remove late fees below.
            </Typography>
            <Box maxHeight={400} overflow="auto" mb={2}>
              {overduePayments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No overdue payments found
                </Typography>
              ) : (
                overduePayments.map((payment) => {
                  // Find all late fees for this payment
                  const lateFees = payments.filter(
                    (p) => p.paymentType === 'Late Fee' && p.originalPaymentId === payment.id
                  );
                  return (
                    <Card key={payment.id} sx={{ mb: 2, p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2">
                            {payment.tenantName} - {payment.apartmentNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Due: {(() => { const d = safeToDate(payment.paymentDate); return d ? d.toLocaleDateString() : 'N/A'; })()} | Amount: {formatCurrency(payment.amount)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box mt={2}>
                        {lateFees.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No late fees yet.
                          </Typography>
                        ) : (
                          lateFees.map((lf) => (
                            <Card key={lf.id} sx={{ mb: 1, p: 1, background: '#fffbe6' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body2">
                                    {lf.description} | ₱{lf.amount}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Created: {new Date(lf.createdAt?.seconds ? lf.createdAt.seconds * 1000 : lf.createdAt).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Box display="flex" gap={1}>
                                  <Button size="small" variant="outlined" color="info" onClick={() => {
                                    setViewLateFee(lf);
                                    setViewLateFeeDialogOpen(true);
                                  }}>
                                    View
                                  </Button>
                                  <Button size="small" variant="outlined" color="error" onClick={async () => {
                                    setLoading(true);
                                    await deleteLateFee(lf.id);
                                    await refreshData();
                                    setLoading(false);
                                  }}>
                                    Remove
                                  </Button>
                                </Box>
                              </Box>
                            </Card>
                          ))
                        )}
                      </Box>
                    </Card>
                  );
                })
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPenaltyDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Late Fee Details Dialog */}
        <Dialog open={viewLateFeeDialogOpen} onClose={() => setViewLateFeeDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Late Fee Details</DialogTitle>
          <DialogContent>
            {viewLateFee && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {viewLateFee.description}
                </Typography>
                <Typography variant="body2">
                  <b>Amount:</b> ₱{viewLateFee.amount}
                </Typography>
                <Typography variant="body2">
                  <b>Status:</b> {viewLateFee.status}
                </Typography>
                <Typography variant="body2">
                  <b>Created:</b> {new Date(viewLateFee.createdAt?.seconds ? viewLateFee.createdAt.seconds * 1000 : viewLateFee.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <b>Payment Method:</b> {viewLateFee.paymentMethod}
                </Typography>
                <Typography variant="body2">
                  <b>Linked Payment ID:</b> {viewLateFee.originalPaymentId}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewLateFeeDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

// Helper function to calculate monthly revenue
const calculateMonthlyRevenue = (payments) => {
  const monthlyData = {};
  
  payments.forEach(payment => {
    if (payment.paymentDate) {
      const date = new Date(payment.paymentDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { name: monthName, Total: 0 };
      }
      monthlyData[monthKey].Total += payment.amount || 0;
    }
  });

  return Object.values(monthlyData).sort((a, b) => {
    const dateA = new Date(a.name);
    const dateB = new Date(b.name);
    return dateA - dateB;
  });
};

// Helper function to calculate payment type breakdown
const calculatePaymentTypeBreakdown = (payments) => {
  const breakdown = {};
  
  payments.forEach(payment => {
    const type = payment.paymentType || 'Other';
    if (!breakdown[type]) {
      breakdown[type] = 0;
    }
    breakdown[type] += payment.amount || 0;
  });

  return Object.entries(breakdown).map(([type, amount]) => ({
    name: type,
    value: amount
  }));
};

// Fix deleteLateFee helper
const deleteLateFee = async (lateFeeId) => {
  try {
    await paymentService.deletePayment(lateFeeId);
  } catch (e) {
    alert('Failed to delete late fee: ' + e.message);
  }
};

// Add safeToDate helper
function safeToDate(val) {
  if (!val) return null;
  if (typeof val === 'object' && val !== null) {
    if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
    if (val instanceof Date) return isNaN(val) ? null : val;
    const d = new Date(val);
    return isNaN(d) ? null : d;
  }
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d) ? null : d;
  }
  const d = new Date(val);
  return isNaN(d) ? null : d;
}

export default Stats; 